import React from 'react';
import ScrollAnimation from '../components/ScrollAnimation';
import './Privacy.css';

const Privacy = () => {
  return (
    <div className="privacy-page">
      <section className="privacy-hero section">
        <div className="container">
          <ScrollAnimation delay={0}>
            <h1 className="page-title">Privacy Policy</h1>
          </ScrollAnimation>
          <ScrollAnimation delay={200}>
            <p className="page-subtitle">
              Your privacy is important to us. Learn how we collect, use, and protect your information.
            </p>
          </ScrollAnimation>
        </div>
      </section>

      <section className="privacy-content section">
        <div className="container">
          <div className="legal-content">
            <ScrollAnimation delay={0}>
              <div className="legal-section">
                <h2>1. Introduction</h2>
                <p>
                  Neeladri Software Solutions Pvt Ltd Private Limited ("Company", "we", "us", or "our") operates 
                  CableBiz, an online billing software platform for cable TV operators and customers. We are committed 
                  to protecting your privacy and ensuring the security of your personal information.
                </p>
                <p>
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
                  use our website and services. Please read this policy carefully to understand our practices regarding 
                  your data.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={100}>
              <div className="legal-section">
                <h2>2. Information We Collect</h2>
                <h3>2.1 Information You Provide</h3>
                <p>We collect information that you voluntarily provide when you:</p>
                <ul>
                  <li>Register for an account (name, email, phone number, business details)</li>
                  <li>Subscribe to a plan (payment information, billing address)</li>
                  <li>Contact our support team (communication records)</li>
                  <li>Use our services (customer data, billing information, transaction records)</li>
                </ul>

                <h3>2.2 Automatically Collected Information</h3>
                <p>When you use CableBiz, we automatically collect certain information, including:</p>
                <ul>
                  <li>Device information (IP address, browser type, operating system)</li>
                  <li>Usage data (pages visited, features used, time spent)</li>
                  <li>Log files (access times, error logs, system events)</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={200}>
              <div className="legal-section">
                <h2>3. How We Use Your Information</h2>
                <p>We use the collected information for various purposes:</p>
                <ul>
                  <li><strong>Service Provision:</strong> To provide, maintain, and improve our services</li>
                  <li><strong>Account Management:</strong> To manage your account and process transactions</li>
                  <li><strong>Communication:</strong> To send you service updates, support responses, and important notices</li>
                  <li><strong>Security:</strong> To detect, prevent, and address technical issues and security threats</li>
                  <li><strong>Analytics:</strong> To analyze usage patterns and improve user experience</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws and regulations</li>
                  <li><strong>Marketing:</strong> To send promotional materials (with your consent, where required)</li>
                </ul>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={300}>
              <div className="legal-section">
                <h2>4. Information Sharing and Disclosure</h2>
                <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
                
                <h3>4.1 Service Providers</h3>
                <p>
                  We may share information with third-party service providers who perform services on our behalf, such as:
                </p>
                <ul>
                  <li>Payment processors</li>
                  <li>Cloud hosting providers</li>
                  <li>Analytics services</li>
                  <li>Customer support platforms</li>
                </ul>
                <p>These providers are contractually obligated to protect your information and use it only for specified purposes.</p>

                <h3>4.2 Legal Requirements</h3>
                <p>We may disclose your information if required by law or in response to:</p>
                <ul>
                  <li>Court orders or legal processes</li>
                  <li>Government requests</li>
                  <li>Enforcement of our Terms & Conditions</li>
                  <li>Protection of our rights, property, or safety</li>
                </ul>

                <h3>4.3 Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred to the 
                  acquiring entity, subject to the same privacy protections.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={400}>
              <div className="legal-section">
                <h2>5. Data Security</h2>
                <p>
                  We implement industry-standard security measures to protect your information from unauthorized access, 
                  alteration, disclosure, or destruction. These measures include:
                </p>
                <ul>
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure authentication and access controls</li>
                  <li>Regular security audits and assessments</li>
                  <li>Employee training on data protection</li>
                  <li>Incident response procedures</li>
                </ul>
                <p>
                  However, no method of transmission over the Internet or electronic storage is 100% secure. While we 
                  strive to protect your data, we cannot guarantee absolute security.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={500}>
              <div className="legal-section">
                <h2>6. Data Retention</h2>
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                  Privacy Policy, unless a longer retention period is required or permitted by law. Factors we consider 
                  when determining retention periods include:
                </p>
                <ul>
                  <li>The nature and sensitivity of the information</li>
                  <li>The purposes for which we process the information</li>
                  <li>Legal and regulatory requirements</li>
                  <li>The potential risk of harm from unauthorized use or disclosure</li>
                </ul>
                <p>
                  When we no longer need your information, we will securely delete or anonymize it in accordance with 
                  our data retention policies.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={600}>
              <div className="legal-section">
                <h2>7. Your Rights and Choices</h2>
                <p>You have certain rights regarding your personal information:</p>
                
                <h3>7.1 Access and Correction</h3>
                <p>
                  You can access and update your account information at any time through your CableBiz account settings. 
                  You may also request a copy of the personal information we hold about you.
                </p>

                <h3>7.2 Data Portability</h3>
                <p>
                  You may request a copy of your data in a structured, machine-readable format.
                </p>

                <h3>7.3 Deletion</h3>
                <p>
                  You may request deletion of your account and associated data, subject to legal and contractual 
                  obligations that may require us to retain certain information.
                </p>

                <h3>7.4 Opt-Out</h3>
                <p>
                  You can opt-out of marketing communications by clicking the unsubscribe link in our emails or by 
                  contacting us directly. Note that you may still receive important service-related communications.
                </p>

                <h3>7.5 Cookies</h3>
                <p>
                  Most web browsers allow you to control cookies through their settings. However, disabling cookies 
                  may limit your ability to use certain features of CableBiz.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={700}>
              <div className="legal-section">
                <h2>8. Children's Privacy</h2>
                <p>
                  CableBiz is not intended for use by individuals under the age of 18. We do not knowingly collect 
                  personal information from children. If we become aware that we have collected information from a 
                  child without parental consent, we will take steps to delete such information promptly.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={800}>
              <div className="legal-section">
                <h2>9. International Data Transfers</h2>
                <p>
                  Your information may be transferred to and processed in countries other than your country of residence. 
                  These countries may have data protection laws that differ from those in your country. By using CableBiz, 
                  you consent to the transfer of your information to these countries.
                </p>
                <p>
                  We take appropriate safeguards to ensure that your information receives an adequate level of protection 
                  regardless of where it is processed.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={900}>
              <div className="legal-section">
                <h2>10. Third-Party Links</h2>
                <p>
                  CableBiz may contain links to third-party websites or services. This Privacy Policy does not apply to 
                  such external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={1000}>
              <div className="legal-section">
                <h2>11. Changes to This Privacy Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or for legal, 
                  operational, or regulatory reasons. We will notify you of any material changes by:
                </p>
                <ul>
                  <li>Posting the updated policy on our website</li>
                  <li>Sending an email notification to registered users</li>
                  <li>Updating the "Last Updated" date at the bottom of this policy</li>
                </ul>
                <p>
                  Your continued use of CableBiz after such changes constitutes your acceptance of the updated Privacy Policy.
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={1100}>
              <div className="legal-section">
                <h2>12. Contact Us</h2>
                <p>
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                  please contact us:
                </p>
                <div className="contact-info">
                  <p><strong>Email:</strong> info@nldsoft.com</p>
                  <p><strong>Privacy Inquiries:</strong> privacy@nldsoft.com</p>
                  <p><strong>Support:</strong> support@nldsoft.com</p>
                  <p><strong>Address:</strong> Plot No: 52, Bapujinagar, Bhubaneswar, Odisha, India - 751009</p>
                </div>
              </div>
            </ScrollAnimation>

            <ScrollAnimation delay={1200}>
              <div className="legal-section">
                <p className="last-updated">
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </ScrollAnimation>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;

